<template>
  <v-dialog :value="dialog" @input="$emit('update:dialog', $event)" max-width="1000" scrollable>
    <v-card class="rounded-xl overflow-hidden">
      <v-toolbar flat color="primary" dark>
        <v-icon left>mdi-folder-account</v-icon>
        <v-toolbar-title class="font-weight-bold">
          Documentos de {{ nombreCliente }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon aria-label="Cerrar documentos" @click="$emit('update:dialog', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4 grey lighten-5">
        <v-card flat outlined class="rounded-lg">
          <v-data-table
            :headers="headers"
            :items="adjuntos"
            :search="search"
            :loading="cargando"
            :items-per-page="10"
            no-data-text="Este cliente no tiene documentos"
            class="rounded-lg"
          >
            <template #top>
              <v-row class="pa-4" align="center">
                <v-col cols="12" sm="8">
                  <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Filtrar documentos..." outlined dense hide-details class="rounded-pill white" />
                </v-col>
                <v-col v-if="puedeAdjuntar" cols="12" sm="4" class="text-right">
                  <v-btn color="primary" block class="rounded-lg" @click="dialogSubir = true">
                    <v-icon left>mdi-cloud-upload</v-icon> Subir Documento
                  </v-btn>
                </v-col>
              </v-row>
            </template>

            <template #item.usuario="{ item }">
              <v-chip small color="blue lighten-5" class="primary--text font-weight-medium">
                <v-icon left x-small>mdi-account</v-icon>{{ nombreUsuario(item.id_usuario) }}
              </v-chip>
            </template>
            <template #item.name="{ item }">
              <div class="text-wrap-cell font-weight-bold">{{ item.name }}</div>
            </template>
            <template #item.descripcion="{ item }">
              <div class="text-wrap-cell grey--text text--darken-2">{{ item.descripcion || '—' }}</div>
            </template>
            <template #item.created_at="{ item }">
              <span class="text-no-wrap">{{ formatFecha(item.created_at) }}</span>
            </template>
            <template #item.actions="{ item }">
              <div class="text-no-wrap">
                <v-btn icon color="primary" title="Previsualizar" :aria-label="`Previsualizar ${item.name}`" @click="ver(item)"><v-icon>mdi-eye-outline</v-icon></v-btn>
                <v-btn icon color="grey darken-1" title="Descargar" :aria-label="`Descargar ${item.name}`" :loading="descargando === item.id" @click="descargar(item)"><v-icon>mdi-download</v-icon></v-btn>
                <v-btn v-if="puedeEliminar" icon color="error lighten-1" title="Eliminar" :aria-label="`Eliminar ${item.name}`" @click="eliminar(item)"><v-icon>mdi-delete-outline</v-icon></v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>
    </v-card>

    <DialogAdjuntosArchivo
      :dialog="dialogSubir"
      @update:dialog="dialogSubir = $event"
      :nodoName="nombreCliente"
      @upload="subir"
    />

    <VisorArchivo ref="visor" />
  </v-dialog>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import DialogAdjuntosArchivo from "./DialogAdjuntosArchivo.vue";
import VisorArchivo from "@/components/utils/VisorArchivo.vue";
import { guardarArchivo } from "@/utils/archivos";

const API = `${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/clientes`;

export default {
  name: "DialogAdjuntosCliente",
  components: { DialogAdjuntosArchivo, VisorArchivo },
  props: {
    dialog: { type: Boolean, default: false },
    idCliente: { type: [Number, String], default: null },
    puedeAdjuntar: { type: Boolean, default: false },
    puedeEliminar: { type: Boolean, default: false },
  },
  data() {
    return {
      adjuntos: [],
      cargando: false,
      search: "",
      dialogSubir: false,
      descargando: null,
      headers: [
        { text: "Usuario", value: "usuario", sortable: false },
        { text: "Nombre", value: "name" },
        { text: "Fecha", value: "created_at" },
        { text: "Descripción", value: "descripcion", sortable: false },
        { text: "Acciones", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    ...mapState(["user", "usuarios", "clientes"]),
    nombreCliente() {
      const cliente = (this.clientes || []).find((c) => Number(c.id_cliente) === Number(this.idCliente));
      return cliente?.nombre || "el cliente";
    },
  },
  watch: {
    // se recarga cada vez que se abre (o cambia el cliente con el diálogo abierto)
    dialog(abierto) {
      if (abierto) this.cargar();
    },
    idCliente() {
      if (this.dialog) this.cargar();
    },
  },
  methods: {
    async cargar() {
      if (!this.idCliente) return;
      this.cargando = true;
      try {
        const { data } = await axios.get(`${API}/${this.idCliente}/adjuntos`);
        this.adjuntos = data.adjuntos || [];
        this.$emit("total", this.adjuntos.length);
      } catch (error) {
        console.error("Error al cargar documentos del cliente:", error);
        this.$emit("notificar", { text: "No se pudieron cargar los documentos", color: "error" });
      } finally {
        this.cargando = false;
      }
    },

    async subir({ nombreArchivo, descripcionArchivo, archivoAdjunto }) {
      const formData = new FormData();
      formData.append("archivo", archivoAdjunto);
      formData.append("name", nombreArchivo);
      formData.append("descripcion", descripcionArchivo || "");
      formData.append("id_usuario", this.user?.id || JSON.parse(localStorage.getItem("user"))?.id);

      try {
        await axios.post(`${API}/${this.idCliente}/adjuntos`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.$emit("notificar", { text: "Documento subido correctamente", color: "success" });
        this.cargar();
      } catch (error) {
        console.error("Error al subir documento del cliente:", error);
        this.$emit("notificar", { text: "Error al subir el documento", color: "error" });
      }
    },

    // Se piden con axios para enviar el token de sesión (window.open o <img> con la URL no lo envían)
    async obtenerBlob(item) {
      const { data } = await axios.get(`${API}/adjuntos/${item.id}/download`, { responseType: "blob" });
      return data;
    },

    /** Ojo: el mismo visor que los adjuntos de los armarios */
    ver(item) {
      this.$refs.visor.abrir(item, () => this.obtenerBlob(item));
    },

    async descargar(item) {
      this.descargando = item.id;
      try {
        guardarArchivo(item, await this.obtenerBlob(item));
      } catch (error) {
        console.error("Error al descargar el documento del cliente:", error);
        this.$emit("notificar", { text: "No se pudo descargar el documento", color: "error" });
      } finally {
        this.descargando = null;
      }
    },

    async eliminar(item) {
      const ok = await this.$confirmar({ titulo: "Eliminar documento", texto: `Se eliminará "${item.name}".`, peligro: true });
      if (!ok) return;
      try {
        await axios.delete(`${API}/adjuntos/${item.id}`);
        this.$emit("notificar", { text: "Documento eliminado", color: "success" });
        this.cargar();
      } catch (error) {
        console.error("Error al eliminar documento del cliente:", error);
        this.$emit("notificar", { text: "No se pudo eliminar el documento", color: "error" });
      }
    },

    nombreUsuario(id_usuario) {
      return (this.usuarios || []).find((u) => u.id === id_usuario)?.username || "Desconocido";
    },

    formatFecha(fechaISO) {
      if (!fechaISO) return "—";
      return new Intl.DateTimeFormat("es-ES", {
        timeZone: "Europe/Madrid",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(fechaISO));
    },
  },
};
</script>

<style scoped>
.text-wrap-cell {
  white-space: normal;
  word-break: break-word;
  max-width: 280px;
}
</style>
